/**
 * Create / edit an automation rule (TRUS-686): "When <event> [and conditions]
 * then <actions>".
 *
 *  - Trigger: an event from the system-event catalog. Org events for org
 *    admins; platform events (RCA, PRs, deploys) only for platform admins the
 *    server allows (`can_subscribe`).
 *  - Conditions: the filters that apply to that event (programs, assignees,
 *    repositories, services). Empty = every event of that type.
 *  - Actions: Post to channel, Notify staff, Create task, Send email. For an
 *    event about members (`phi`), Post to channel only lists private channels.
 *    Every template may only use the allowed variables.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Bell, Hash, ListChecks, Lock, Mail, Plus, Smartphone, Trash2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { StaffMultiSelect } from '@/components/forms/StaffMultiSelect';
import { useOrgStaffOptions } from '@/components/forms/staffOptions';
import {
  EMPTY_FILTERS,
  type SystemEventType,
} from '@/services/systemEventsApi';
import {
  AUTOMATION_ACTION_TYPES,
  type AutomationAction,
  type AutomationActionType,
  type AutomationRule,
  type AutomationRuleInput,
  type TaskAssignee,
} from '@/services/automationRulesApi';
import { EventFilterFields } from '@/components/messaging/systemEvents/EventFilterFields';
import { filtersOfType, groupLabel, groupTypes, typeLabel, type TFn } from '@/components/messaging/systemEvents/eventCatalog';
import { ChannelPicker } from '../notify/ChannelPicker';
import { MessageTemplateEditor } from '../notify/MessageTemplateEditor';
import { StaffRecipientsEditor, useOrgRoleOptions } from '../notify/StaffRecipientsEditor';
import { hasRecipients, unknownTemplateVariables } from '../notify/notifySteps';

// ---------------------------------------------------------------------------
// Pure helpers (tested directly)
// ---------------------------------------------------------------------------

/** Trigger events this viewer may pick. */
export function triggerTypesFor(types: SystemEventType[], isPlatformAdmin: boolean): SystemEventType[] {
  return types.filter((x) => x.scope === 'org' || (isPlatformAdmin && x.can_subscribe));
}

export function newAction(type: AutomationActionType): AutomationAction {
  switch (type) {
    case 'post_to_channel':
      return { type, channel_id: null, message_template: '' };
    case 'notify_staff':
      return { type, recipients: { assignee: true }, channels: ['bell', 'push'], message_template: '' };
    case 'create_task':
      return { type, title_template: '', assignee: { assignee_of_event: true }, due_in_days: 1 };
    case 'send_email':
    default:
      return { type: 'send_email', to_user_ids: [], roles: [], subject_template: '', body_template: '' };
  }
}

export type RuleIssue =
  | { code: 'name_required' | 'trigger_required' | 'actions_required' }
  | { code: 'channel_required' | 'recipients_required' | 'delivery_required' | 'title_required' | 'assignee_required' | 'email_recipients_required' | 'subject_required' | 'body_required'; action: number }
  | { code: 'unknown_variables'; action: number; variables: string[] };

const templatesOf = (a: AutomationAction): string[] => {
  switch (a.type) {
    case 'post_to_channel':
    case 'notify_staff':
      return [a.message_template || ''];
    case 'create_task':
      return [a.title_template];
    case 'send_email':
      return [a.subject_template, a.body_template];
    default:
      return [];
  }
};

export function ruleIssues(input: AutomationRuleInput): RuleIssue[] {
  const issues: RuleIssue[] = [];
  if (!input.name.trim()) issues.push({ code: 'name_required' });
  if (!input.trigger.event_type) issues.push({ code: 'trigger_required' });
  if (input.actions.length === 0) issues.push({ code: 'actions_required' });
  input.actions.forEach((a, i) => {
    if (a.type === 'post_to_channel' && !(Number(a.channel_id) > 0)) issues.push({ code: 'channel_required', action: i });
    if (a.type === 'notify_staff') {
      if (!hasRecipients(a.recipients)) issues.push({ code: 'recipients_required', action: i });
      if (!a.channels?.length) issues.push({ code: 'delivery_required', action: i });
    }
    if (a.type === 'create_task') {
      if (!a.title_template.trim()) issues.push({ code: 'title_required', action: i });
      const as = a.assignee as any;
      if (('user_id' in as && !(as.user_id > 0)) || ('role' in as && !String(as.role || '').trim())) {
        issues.push({ code: 'assignee_required', action: i });
      }
    }
    if (a.type === 'send_email') {
      if (!(a.to_user_ids?.length || a.roles?.length)) issues.push({ code: 'email_recipients_required', action: i });
      if (!a.subject_template.trim()) issues.push({ code: 'subject_required', action: i });
      if (!a.body_template.trim()) issues.push({ code: 'body_required', action: i });
    }
    const unknown = templatesOf(a).flatMap((tpl) => unknownTemplateVariables(tpl));
    if (unknown.length) issues.push({ code: 'unknown_variables', action: i, variables: [...new Set(unknown)] });
  });
  return issues;
}

export function ruleIssueMessage(issue: RuleIssue, t: TFn): string {
  const n = 'action' in issue ? issue.action + 1 : 0;
  switch (issue.code) {
    case 'name_required':
      return t('workflows.automations.issues.name', 'Give the automation a name.');
    case 'trigger_required':
      return t('workflows.automations.issues.trigger', 'Pick the event that starts it.');
    case 'actions_required':
      return t('workflows.automations.issues.actions', 'Add at least one action.');
    case 'channel_required':
      return t('workflows.automations.issues.channel', 'Action {{n}}: pick the channel to post to.', { n });
    case 'recipients_required':
      return t('workflows.automations.issues.recipients', 'Action {{n}}: pick at least one recipient.', { n });
    case 'delivery_required':
      return t('workflows.automations.issues.delivery', 'Action {{n}}: turn on the bell, push, or both.', { n });
    case 'title_required':
      return t('workflows.automations.issues.title', 'Action {{n}}: write the task title.', { n });
    case 'assignee_required':
      return t('workflows.automations.issues.assignee', 'Action {{n}}: pick who gets the task.', { n });
    case 'email_recipients_required':
      return t('workflows.automations.issues.emailTo', 'Action {{n}}: pick who gets the email.', { n });
    case 'subject_required':
      return t('workflows.automations.issues.subject', 'Action {{n}}: write the email subject.', { n });
    case 'body_required':
      return t('workflows.automations.issues.body', 'Action {{n}}: write the email.', { n });
    case 'unknown_variables':
      return t('workflows.automations.issues.variables', 'Action {{n}}: these variables are not allowed and would be left blank: {{list}}', {
        n,
        list: issue.variables.map((v) => `{{${v}}}`).join(', '),
      });
    default:
      return '';
  }
}

export function actionTypeLabel(type: AutomationActionType, t: TFn): string {
  switch (type) {
    case 'post_to_channel':
      return t('workflows.automations.actions.postToChannel', 'Post to channel');
    case 'notify_staff':
      return t('workflows.automations.actions.notifyStaff', 'Notify staff');
    case 'create_task':
      return t('workflows.automations.actions.createTask', 'Create task');
    case 'send_email':
      return t('workflows.automations.actions.sendEmail', 'Send email');
    default:
      return type;
  }
}

const ACTION_ICON: Record<AutomationActionType, React.ComponentType<{ className?: string }>> = {
  post_to_channel: Hash,
  notify_staff: Bell,
  create_task: ListChecks,
  send_email: Mail,
};

export const emptyRuleInput = (): AutomationRuleInput => ({
  name: '',
  is_active: true,
  trigger: { event_type: '', conditions: { ...EMPTY_FILTERS } },
  actions: [],
});

export const ruleToInput = (rule: AutomationRule): AutomationRuleInput => ({
  name: rule.name,
  is_active: rule.is_active,
  trigger: { event_type: rule.trigger.event_type, conditions: { ...EMPTY_FILTERS, ...rule.trigger.conditions } },
  actions: rule.actions.map((a) => ({ ...a })),
});

// ---------------------------------------------------------------------------
// Action editors
// ---------------------------------------------------------------------------

function ActionEditor({
  index,
  action,
  onChange,
  phi,
}: {
  index: number;
  action: AutomationAction;
  onChange: (next: AutomationAction) => void;
  phi: boolean;
}) {
  const { t } = useAppTranslation();
  const staff = useOrgStaffOptions({ includeOrgAdmins: true });
  const roles = useOrgRoleOptions();
  const id = `automation-action-${index}`;
  const pickerText = {
    searchPlaceholder: t('workflows.notify.search', 'Search…'),
    clearLabel: t('workflows.notify.clear', 'Clear all'),
    noMatchLabel: (q: string) => t('workflows.notify.noMatch', 'No match for “{{query}}”', { query: q }),
    removeLabel: (label: string) => t('workflows.notify.remove', 'Remove {{name}}', { name: label }),
  };

  switch (action.type) {
    case 'post_to_channel':
      return (
        <div className="space-y-3">
          <ChannelPicker
            id={`${id}-channel`}
            value={action.channel_id}
            onChange={(channel_id) => onChange({ ...action, channel_id })}
            privateOnly={phi}
          />
          <MessageTemplateEditor
            id={`${id}-message`}
            label={t('workflows.automations.messageOptional', 'Message (optional — the event title is used when empty)')}
            value={action.message_template || ''}
            onChange={(message_template) => onChange({ ...action, message_template })}
          />
        </div>
      );
    case 'notify_staff': {
      const channels = action.channels || [];
      return (
        <div className="space-y-3">
          <StaffRecipientsEditor
            idPrefix={id}
            value={action.recipients}
            onChange={(recipients) => onChange({ ...action, recipients })}
            allowCaseManager={phi}
            assigneeLabel={t('workflows.automations.eventAssignee', 'Whoever the event is assigned to')}
          />
          <fieldset className="flex flex-wrap gap-4">
            <legend className="mb-1 text-sm font-medium">{t('workflows.notify.deliverBy', 'Notify by')}</legend>
            {(
              [
                ['bell', Bell, t('workflows.notify.bell', 'Bell')],
                ['push', Smartphone, t('workflows.notify.push', 'Push')],
              ] as const
            ).map(([value, Icon, label]) => (
              <label key={value} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--primary,#2563EB)]"
                  checked={channels.includes(value)}
                  onChange={(e) =>
                    onChange({
                      ...action,
                      channels: e.target.checked
                        ? [...channels.filter((c) => c !== value), value]
                        : channels.filter((c) => c !== value),
                    })
                  }
                  data-testid={`${id}-deliver-${value}`}
                />
                <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                {label}
              </label>
            ))}
          </fieldset>
          <MessageTemplateEditor
            id={`${id}-message`}
            label={t('workflows.automations.messageOptional', 'Message (optional — the event title is used when empty)')}
            value={action.message_template || ''}
            onChange={(message_template) => onChange({ ...action, message_template })}
          />
        </div>
      );
    }
    case 'create_task': {
      const as = action.assignee as any;
      const mode: 'event' | 'user' | 'role' = 'user_id' in as ? 'user' : 'role' in as ? 'role' : 'event';
      const setMode = (m: 'event' | 'user' | 'role') => {
        const next: TaskAssignee =
          m === 'user' ? { user_id: 0 } : m === 'role' ? { role: '' } : { assignee_of_event: true };
        onChange({ ...action, assignee: next });
      };
      return (
        <div className="space-y-3">
          <MessageTemplateEditor
            id={`${id}-title`}
            label={t('workflows.automations.taskTitle', 'Task title')}
            value={action.title_template}
            onChange={(title_template) => onChange({ ...action, title_template })}
            singleLine
          />
          <fieldset className="space-y-1.5">
            <legend className="mb-1 text-sm font-medium">{t('workflows.automations.taskAssignee', 'Assign the task to')}</legend>
            {(
              [
                ['event', t('workflows.automations.assignEvent', 'Whoever the event is assigned to')],
                ['user', t('workflows.automations.assignUser', 'A person')],
                ['role', t('workflows.automations.assignRole', 'Someone with a role')],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name={`${id}-assignee`}
                  className="h-4 w-4 accent-[var(--primary,#2563EB)]"
                  checked={mode === value}
                  onChange={() => setMode(value)}
                  data-testid={`${id}-assignee-${value}`}
                />
                {label}
              </label>
            ))}
            {mode === 'user' && (
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={as.user_id > 0 ? String(as.user_id) : ''}
                onChange={(e) => onChange({ ...action, assignee: { user_id: Number(e.target.value) || 0 } })}
                aria-label={t('workflows.automations.assignUser', 'A person')}
                data-testid={`${id}-assignee-user`}
              >
                <option value="">{t('workflows.automations.pickPerson', 'Pick a person')}</option>
                {staff.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
            {mode === 'role' && (
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={String(as.role || '')}
                onChange={(e) => onChange({ ...action, assignee: { role: e.target.value } })}
                aria-label={t('workflows.automations.assignRole', 'Someone with a role')}
                data-testid={`${id}-assignee-role`}
              >
                <option value="">{t('workflows.automations.pickRole', 'Pick a role')}</option>
                {roles.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </fieldset>
          <div className="space-y-1">
            <label htmlFor={`${id}-due`} className="block text-sm font-medium">
              {t('workflows.automations.dueInDays', 'Due in (days, optional)')}
            </label>
            <Input
              id={`${id}-due`}
              type="number"
              min={0}
              max={365}
              className="w-32"
              value={action.due_in_days ?? ''}
              onChange={(e) =>
                onChange({ ...action, due_in_days: e.target.value === '' ? null : Math.max(0, Number(e.target.value)) })
              }
            />
          </div>
        </div>
      );
    }
    case 'send_email':
      return (
        <div className="space-y-3">
          <div className="space-y-1">
            <span className="block text-sm font-medium">{t('workflows.automations.emailPeople', 'Email these people')}</span>
            <StaffMultiSelect
              options={staff}
              value={(action.to_user_ids || []).map(String)}
              onChange={(next) => onChange({ ...action, to_user_ids: next.map(Number).filter((n) => n > 0) })}
              ariaLabel={t('workflows.automations.emailPeople', 'Email these people')}
              placeholder={t('workflows.notify.noPeople', 'No one else')}
              emptyHint={t('workflows.notify.peopleEmpty', 'No staff found')}
              {...pickerText}
            />
          </div>
          <div className="space-y-1">
            <span className="block text-sm font-medium">{t('workflows.automations.emailRoles', 'and everyone with these roles')}</span>
            <StaffMultiSelect
              options={roles}
              value={action.roles || []}
              onChange={(next) => onChange({ ...action, roles: next })}
              ariaLabel={t('workflows.automations.emailRoles', 'and everyone with these roles')}
              placeholder={t('workflows.notify.noRoles', 'No roles')}
              emptyHint={t('workflows.notify.rolesEmpty', 'No roles found')}
              {...pickerText}
            />
          </div>
          <MessageTemplateEditor
            id={`${id}-subject`}
            label={t('workflows.automations.subject', 'Subject')}
            value={action.subject_template}
            onChange={(subject_template) => onChange({ ...action, subject_template })}
            singleLine
          />
          <MessageTemplateEditor
            id={`${id}-body`}
            label={t('workflows.automations.body', 'Email')}
            value={action.body_template}
            onChange={(body_template) => onChange({ ...action, body_template })}
            rows={5}
          />
        </div>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

interface AutomationRuleEditorProps {
  initial: AutomationRuleInput;
  types: SystemEventType[];
  isPlatformAdmin: boolean;
  saving: boolean;
  onSave: (input: AutomationRuleInput) => void;
  onCancel: () => void;
}

export function AutomationRuleEditor({ initial, types, isPlatformAdmin, saving, onSave, onCancel }: AutomationRuleEditorProps) {
  const { t } = useAppTranslation();
  const [input, setInput] = useState<AutomationRuleInput>(initial);
  const [addType, setAddType] = useState<AutomationActionType>('post_to_channel');
  const [tried, setTried] = useState(false);

  useEffect(() => setInput(initial), [initial]);

  const offered = useMemo(() => {
    const allowed = triggerTypesFor(types, isPlatformAdmin);
    // Keep a saved trigger visible even if it is no longer offered.
    const current = types.find((x) => x.type === input.trigger.event_type);
    return current && !allowed.includes(current) ? [...allowed, current] : allowed;
  }, [types, isPlatformAdmin, input.trigger.event_type]);
  const groups = useMemo(() => groupTypes(offered), [offered]);
  const triggerType = types.find((x) => x.type === input.trigger.event_type);
  const phi = triggerType?.phi === true;
  const kinds = useMemo(
    () => new Set(triggerType ? filtersOfType(triggerType) : []),
    [triggerType],
  );
  const issues = ruleIssues(input);

  const setAction = (i: number, next: AutomationAction) =>
    setInput((s) => ({ ...s, actions: s.actions.map((a, j) => (j === i ? next : a)) }));

  const submit = () => {
    setTried(true);
    if (issues.length > 0) return;
    // Only the conditions that apply to the chosen event are sent.
    const c = input.trigger.conditions;
    onSave({
      ...input,
      trigger: {
        event_type: input.trigger.event_type,
        conditions: {
          programs: kinds.has('programs') ? c.programs : [],
          assignees: kinds.has('assignees') ? c.assignees.filter((a) => a !== 'me') : [],
          repos: kinds.has('repos') ? c.repos : [],
          services: kinds.has('services') ? c.services : [],
        },
      },
    });
  };

  return (
    <div className="space-y-5" data-testid="automation-rule-editor">
      <div className="space-y-1">
        <label htmlFor="automation-name" className="block text-sm font-medium">
          {t('workflows.automations.name', 'Name')}
        </label>
        <Input
          id="automation-name"
          value={input.name}
          onChange={(e) => setInput((s) => ({ ...s, name: e.target.value }))}
          placeholder={t('workflows.automations.namePlaceholder', 'e.g. Tell the care team when a consent is signed')}
          disabled={saving}
        />
      </div>

      <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
        <label htmlFor="automation-active" className="text-sm font-medium">
          {input.is_active ? t('workflows.automations.enabled', 'Enabled') : t('workflows.automations.disabled', 'Disabled')}
        </label>
        <Switch
          id="automation-active"
          checked={input.is_active}
          onCheckedChange={(v: boolean) => setInput((s) => ({ ...s, is_active: !!v }))}
          aria-label={t('workflows.automations.enableLabel', 'Run this automation')}
          disabled={saving}
        />
      </div>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold">{t('workflows.automations.when', 'When')}</h3>
        <select
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={input.trigger.event_type}
          onChange={(e) =>
            setInput((s) => ({ ...s, trigger: { event_type: e.target.value, conditions: { ...EMPTY_FILTERS } } }))
          }
          aria-label={t('workflows.automations.triggerLabel', 'Event that starts the automation')}
          disabled={saving}
          data-testid="automation-trigger"
        >
          <option value="">{t('workflows.automations.pickEvent', 'Pick an event')}</option>
          {groups.map((g) => (
            <optgroup key={g.id} label={groupLabel(g.id, t)}>
              {g.types.map((x) => (
                <option key={x.type} value={x.type}>
                  {typeLabel(x.type, types, t)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {phi && (
          <p className="flex items-start gap-1 text-xs text-muted-foreground" data-testid="automation-phi-note">
            <Lock className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
            {t(
              'workflows.automations.phiNote',
              'This event is about members: channel posts only go to private channels, and messages can only use the variables below.',
            )}
          </p>
        )}
        {triggerType && kinds.size > 0 && (
          <div className="space-y-2 rounded-lg border p-3">
            <p className="text-xs text-muted-foreground">
              {t('workflows.automations.conditionsHint', 'Only when… (leave empty for every event of this kind)')}
            </p>
            <EventFilterFields
              kinds={kinds}
              value={input.trigger.conditions}
              onChange={(conditions) => setInput((s) => ({ ...s, trigger: { ...s.trigger, conditions } }))}
              disabled={saving}
              idPrefix="automation-conditions"
            />
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold">{t('workflows.automations.then', 'Then')}</h3>
        {input.actions.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('workflows.automations.noActions', 'No actions yet.')}</p>
        )}
        {input.actions.map((action, i) => {
          const Icon = ACTION_ICON[action.type];
          return (
            <div key={i} className="space-y-3 rounded-lg border p-3" data-testid={`automation-action-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
                  {t('workflows.automations.actionN', '{{n}}. {{action}}', { n: i + 1, action: actionTypeLabel(action.type, t) })}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setInput((s) => ({ ...s, actions: s.actions.filter((_, j) => j !== i) }))}
                  aria-label={t('workflows.automations.removeAction', 'Remove action {{n}}', { n: i + 1 })}
                  disabled={saving}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
              <ActionEditor index={i} action={action} onChange={(next) => setAction(i, next)} phi={phi} />
            </div>
          );
        })}
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="flex h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={addType}
            onChange={(e) => setAddType(e.target.value as AutomationActionType)}
            aria-label={t('workflows.automations.actionType', 'Action to add')}
            disabled={saving}
            data-testid="automation-add-type"
          >
            {AUTOMATION_ACTION_TYPES.map((type) => (
              <option key={type} value={type}>
                {actionTypeLabel(type, t)}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setInput((s) => ({ ...s, actions: [...s.actions, newAction(addType)] }))}
            disabled={saving}
            data-testid="automation-add-action"
          >
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
            {t('workflows.automations.addAction', 'Add action')}
          </Button>
        </div>
      </section>

      {tried && issues.length > 0 && (
        <ul className="space-y-0.5 text-xs text-destructive" role="alert" data-testid="automation-issues">
          {issues.map((issue, i) => (
            <li key={i}>{ruleIssueMessage(issue, t)}</li>
          ))}
        </ul>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          {t('workflows.automations.cancel', 'Cancel')}
        </Button>
        <Button type="button" onClick={submit} disabled={saving} data-testid="automation-save">
          {t('workflows.automations.save', 'Save')}
        </Button>
      </div>
    </div>
  );
}

export default AutomationRuleEditor;
